import React, { useEffect, useState } from 'react'
import Layout from '@/components/layout/Layout'
import MainHeading from '@/components/common/MainHeading'
import Card from '@/components/common/Card'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useNavigate, useParams } from 'react-router-dom'
import { LeaveService } from '@/services/Leave/LeaveSetting'
import { ThrowError } from '@/api/callApi'
import type { ILeave } from '@/interface/leave'
import moment from 'moment'
import { ArrowLeft, Calendar, FileText, Info, User } from 'lucide-react'

const LeaveDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [leave, setLeave] = useState<ILeave | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const getLeaveDetails = async () => {
        if (!id) return;
        try {
            setIsLoading(true);
            const response = await LeaveService.getLeaveById(id);
            if (response?.status && response.data) {
                setLeave(response.data);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getLeaveDetails();
    }, [id]);

    if (isLoading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Spinner className="h-8 w-8 text-primary animate-spin" />
                </div>
            </Layout>
        );
    }

    if (!leave) {
        return (
            <Layout>
                <div className="text-center py-12">
                    <h3 className="text-lg font-semibold text-gray-700">Leave details not found</h3>
                    <Button onClick={() => navigate(-1)} className="mt-4">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>
                </div>
            </Layout>
        );
    }

    const status = (leave.status || "pending").toLowerCase();
    let statusClasses = "text-amber-700 bg-amber-50 border-amber-200";
    if (status === "approved" || status === "active") {
        statusClasses = "text-green-700 bg-green-50 border-green-200";
    } else if (status === "rejected" || status === "cancelled") {
        statusClasses = "text-destructive bg-destructive/10 border-destructive/20";
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <MainHeading title="Leave Detail" isBackButtonVisible onClick={() => navigate(-1)} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column: Leave Details */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card>
                            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                                <Info className="w-5 h-5 text-primary" />
                                <h3 className="font-display text-base font-semibold">Leave Information</h3>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                                <InfoItem label="Leave Type" value={leave.leaveType} />
                                <InfoItem label="Duration Type" value={leave.duration ? leave.duration.replace(/_/g, " ") : "—"} />
                                <InfoItem label="Start Date" value={leave.startDate ? moment(leave.startDate).format("DD-MM-YYYY") : "—"} />
                                <InfoItem label="End Date" value={leave.endDate ? moment(leave.endDate).format("DD-MM-YYYY") : "—"} />
                                <InfoItem label="Number of Days" value={leave.numberOfDays} />
                                <div>
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Status</p>
                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase border capitalize ${statusClasses}`}>
                                        {status}
                                    </span>
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                                <FileText className="w-5 h-5 text-primary" />
                                <h3 className="font-display text-base font-semibold">Reason for Leave</h3>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                                {leave.reason || "No reason specified."}
                            </p>
                        </Card>
                    </div>

                    {/* Right Column: Metadata / Employee profile summary */}
                    <div className="space-y-6">
                        <Card>
                            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                                <User className="w-5 h-5 text-primary" />
                                <h3 className="font-display text-base font-semibold">Applicant Details</h3>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Employee Name</p>
                                    <p className="text-sm font-semibold text-foreground">{leave.user?.name || "Logged-in User"}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Email Address</p>
                                    <p className="text-sm font-medium text-slate-600">{leave.user?.email || "—"}</p>
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                                <Calendar className="w-5 h-5 text-primary" />
                                <h3 className="font-display text-base font-semibold">Timeline Details</h3>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Applied On</p>
                                    <p className="text-sm font-medium text-slate-600">
                                        {leave.createdAt ? moment(leave.createdAt).format("DD-MM-YYYY HH:mm") : "—"}
                                    </p>
                                </div>
                                {leave.updatedAt && leave.updatedAt !== leave.createdAt && (
                                    <div>
                                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Last Updated</p>
                                        <p className="text-sm font-medium text-slate-600">
                                            {moment(leave.updatedAt).format("DD-MM-YYYY HH:mm")}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

const InfoItem: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
    <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm font-semibold text-foreground capitalize">{value || '—'}</p>
    </div>
);

export default LeaveDetail